const GCP_FILE_PROXY_PATH = "/api/gcpfile/";
const WEB_PDF_PROXY_PATH = "/api/web-pdf/";

const PRIVATE_GCP_PATH_MARKERS = [
  "user_private_files",
  "user_file_model_retrievals",
  "morningstar_reports",
  "morningstar_reports_model_retrievals",
  "finster-bluematrix-research",
  "finster-bluematrix-research-model-retrievals",
  "adia-crm-docs",
  "adia-crm-docs-model-retrievals",
  "adia-investment-docs",
  "adia-investment-docs-model-retrievals",
  "model_generated_files_public",
  "model_generated_files_public_1day_ttl",
  "source_information_documents",
  "finster_shared_documents_public",
  "temp_ir_docs",
  "user_private_files_internal_html",
  "investor_relations_documents",
  "investor-relations-documents",
  "ir_documents",
  "ir-documents",
  "finster-ir-docs",
  "finster-ir-documents",
] as const;

function normalizeUrl(value: string | null | undefined): string {
  return String(value || "").trim();
}

function getAppOrigin(): string {
  if (typeof window === "undefined" || !window.location?.origin) return "";
  return window.location.origin;
}

function withAppOrigin(path: string): string {
  const origin = getAppOrigin();
  return origin ? `${origin}${path}` : path;
}

function getPathname(value: string): string {
  try {
    return new URL(value, getAppOrigin() || "https://localhost").pathname;
  } catch {
    return "";
  }
}

function isProxiedGcpFileUrl(value: string): boolean {
  return getPathname(value).startsWith(GCP_FILE_PROXY_PATH);
}

function isProxiedWebPdfUrl(value: string): boolean {
  return getPathname(value).startsWith(WEB_PDF_PROXY_PATH);
}

function isRawGcsUrl(value: string): boolean {
  const url = value.toLowerCase();
  return (
    url.startsWith("gs://") ||
    url.startsWith("gcs://") ||
    url.includes("storage.googleapis.com/") ||
    url.includes("storage.cloud.google.com/")
  );
}

export function isPrivateGcpFileUrl(rawUrl: string | null | undefined): boolean {
  const url = normalizeUrl(rawUrl).toLowerCase();
  if (!url) return false;
  if (isProxiedGcpFileUrl(url)) return true;
  if (PRIVATE_GCP_PATH_MARKERS.some((marker) => url.includes(marker))) return true;

  // Some source records surface raw GCS URLs whose bucket path does not
  // match the known path markers. Route those through the authenticated proxy too.
  return isRawGcsUrl(url);
}

export function resolveCitationDocumentUrl(rawUrl: string | null | undefined): string {
  const url = normalizeUrl(rawUrl);
  if (!url) return "";

  if (isProxiedGcpFileUrl(url)) {
    return url.startsWith(GCP_FILE_PROXY_PATH) ? withAppOrigin(url) : url;
  }

  if (isPrivateGcpFileUrl(url)) {
    return withAppOrigin(`${GCP_FILE_PROXY_PATH}${encodeURIComponent(url)}`);
  }

  return url;
}

export function resolveCitationPdfUrl(rawUrl: string | null | undefined): string {
  const url = normalizeUrl(rawUrl);
  if (!url) return "";

  if (isProxiedWebPdfUrl(url)) {
    return url.startsWith(WEB_PDF_PROXY_PATH) ? withAppOrigin(url) : url;
  }

  if (isProxiedGcpFileUrl(url)) {
    return url.startsWith(GCP_FILE_PROXY_PATH) ? withAppOrigin(url) : url;
  }

  const documentUrl = resolveCitationDocumentUrl(url);
  if (documentUrl !== url) {
    return documentUrl;
  }

  if (/^https?:\/\//i.test(url)) {
    return withAppOrigin(`${WEB_PDF_PROXY_PATH}${encodeURIComponent(url)}`);
  }

  return url;
}
